import { useState, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import CommentForm from './CommentForm';
import CommentItem from './CommentItem';

interface Comment {
  id: string;
  user_id: string;
  user_name: string;
  content: string;
  is_private: boolean;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
}

interface CommentWithReplies extends Comment {
  replies?: Comment[];
}

interface CommentSectionProps {
  classId: string;
  providerId: string; // 수업 등록 업체 ID
}

export default function CommentSection({ classId, providerId }: CommentSectionProps) {
  const { user, isAuthenticated } = useAuthStore();
  const [comments, setComments] = useState<CommentWithReplies[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isProvider = user?.id === providerId || user?.role === 'admin';

  // 댓글 목록 조회
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await fetch(`/api/v1/classes/${classId}/comments`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('댓글 목록 조회에 실패했습니다.');
        }

        const data = await response.json();

        // 댓글을 계층 구조로 변환 (최상위 댓글 + 답글)
        const commentMap = new Map<string, CommentWithReplies>();
        const topLevelComments: CommentWithReplies[] = [];

        // 1. 모든 댓글을 맵에 저장
        data.comments.forEach((comment: Comment) => {
          commentMap.set(comment.id, { ...comment, replies: [] });
        });

        // 2. 계층 구조 생성
        data.comments.forEach((comment: Comment) => {
          const commentWithReplies = commentMap.get(comment.id)!;

          if (comment.parent_id) {
            // 답글인 경우 부모 댓글의 replies에 추가
            const parent = commentMap.get(comment.parent_id);
            if (parent) {
              parent.replies = parent.replies || [];
              parent.replies.push(commentWithReplies);
            }
          } else {
            // 최상위 댓글
            topLevelComments.push(commentWithReplies);
          }
        });

        setComments(topLevelComments);
      } catch (err: any) {
        console.error('Failed to fetch comments:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [classId]);

  // 새 댓글 작성
  const handleCreateComment = async (content: string, isPrivate: boolean) => {
    if (!isAuthenticated) {
      alert('로그인이 필요합니다.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/v1/classes/${classId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ content, isPrivate }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '댓글 작성에 실패했습니다.');
      }

      const data = await response.json();

      // 새 댓글을 목록에 추가 (API 응답에 user_name 포함)
      setComments((prev) => [
        {
          ...data.comment,
          replies: [],
        },
        ...prev,
      ]);
    } catch (err: any) {
      console.error('Failed to create comment:', err);
      setError(err.message);
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // 답글 작성
  const handleCreateReply = async (
    parentId: string,
    content: string,
    isPrivate: boolean
  ) => {
    if (!isAuthenticated) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      const response = await fetch(`/api/v1/classes/${classId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ content, isPrivate, parentId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '답글 작성에 실패했습니다.');
      }

      const data = await response.json();

      // 답글을 부모 댓글의 replies에 추가 (API 응답에 user_name 포함)
      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id === parentId) {
            return {
              ...comment,
              replies: [
                ...(comment.replies || []),
                data.comment,
              ],
            };
          }
          return comment;
        })
      );
    } catch (err: any) {
      console.error('Failed to create reply:', err);
      alert(err.message);
    }
  };

  // 댓글 수정
  const handleEditComment = async (commentId: string, content: string) => {
    try {
      const response = await fetch(`/api/v1/classes/${classId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '댓글 수정에 실패했습니다.');
      }

      const data = await response.json();

      // 댓글 목록 업데이트 (최상위 댓글 또는 답글)
      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id === commentId) {
            return { ...comment, content: data.comment.content, updated_at: data.comment.updated_at };
          }
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.map((reply) =>
                reply.id === commentId
                  ? { ...reply, content: data.comment.content, updated_at: data.comment.updated_at }
                  : reply
              ),
            };
          }
          return comment;
        })
      );
    } catch (err: any) {
      console.error('Failed to edit comment:', err);
      alert(err.message);
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (commentId: string) => {
    try {
      const response = await fetch(`/api/v1/classes/${classId}/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '댓글 삭제에 실패했습니다.');
      }

      // 댓글 목록에서 제거 (최상위 댓글 또는 답글)
      setComments((prev) =>
        prev
          .filter((comment) => comment.id !== commentId)
          .map((comment) => {
            if (comment.replies) {
              return {
                ...comment,
                replies: comment.replies.filter((reply) => reply.id !== commentId),
              };
            }
            return comment;
          })
      );
    } catch (err: any) {
      console.error('Failed to delete comment:', err);
      alert(err.message);
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-6">
      {/* 헤더 */}
      <div className="mb-6 flex items-center gap-2">
        <MessageSquare className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold text-gray-900">
          댓글 <span className="text-primary">({comments.length})</span>
        </h2>
      </div>

      {/* 댓글 작성 폼 */}
      {isAuthenticated ? (
        <div className="mb-6">
          <CommentForm onSubmit={handleCreateComment} loading={submitting} />
        </div>
      ) : (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 text-center">
          <p className="text-gray-600">댓글을 작성하려면 로그인해주세요.</p>
        </div>
      )}

      {/* 에러 메시지 */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* 로딩 */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : comments.length === 0 ? (
        /* 빈 상태 */
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">아직 댓글이 없습니다</h3>
          <p className="text-gray-500">첫 번째 댓글을 작성해보세요!</p>
        </div>
      ) : (
        /* 댓글 목록 */
        <div className="space-y-4">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              classId={classId}
              currentUserId={user?.id || null}
              isProvider={isProvider}
              onEdit={handleEditComment}
              onDelete={handleDeleteComment}
              onReply={handleCreateReply}
            />
          ))}
        </div>
      )}
    </div>
  );
}
